import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import CampoFirma from "@/components/CampoFirma";
import {
  CheckCircle2, AlertTriangle, Clock, Loader2, PenLine, Upload, ShieldAlert,
  ScrollText, HardHat, Lock, Unlock, FileCheck2,
} from "lucide-react";

/**
 * La pratica di assunzione.
 *
 * Non e' un modulo da compilare: e' l'elenco dei sette vincoli, e ogni riga
 * guarda il fatto vero dove sta gia' - la consegna firmata, il documento nel
 * fascicolo, la scheda chiusa. Per questo si riapre quante volte si vuole e
 * dice sempre la verita': non c'e' niente da ricordarsi di spuntare.
 *
 * Finche' una riga e' rossa la persona in cantiere non ci va, e il rifiuto
 * arriva dal server anche se qualcuno ci prova da un'altra schermata.
 */
const SEGNO = {
  ok: { icona: CheckCircle2, classe: "text-emerald-600",
        riquadro: "border-emerald-200 bg-emerald-50/50" },
  in_attesa: { icona: Clock, classe: "text-amber-600",
               riquadro: "border-amber-200 bg-amber-50/60" },
  manca: { icona: AlertTriangle, classe: "text-red-600",
           riquadro: "border-red-200 bg-red-50/50" },
};

export default function PraticaAssunzione({ emp, isAdmin, onChiudi, onCambio }) {
  const [pratica, setPratica] = useState(null);
  const [carico, setCarico] = useState(true);
  const [nota, setNota] = useState("");
  const [firma, setFirma] = useState(null);      // "tesserino" | "gps"
  const [carica, setCarica] = useState(null);    // "personale" | "attestato"
  const [deroga, setDeroga] = useState(false);
  const [salvo, setSalvo] = useState(false);

  const leggi = useCallback(async () => {
    try {
      const { data } = await api.get(`/assunzione/${emp.id}`);
      setPratica(data);
      setNota(data.nota || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento della pratica");
    } finally { setCarico(false); }
  }, [emp.id]);

  useEffect(() => { leggi(); }, [leggi]);

  const aggiorna = (data) => {
    setPratica(data);
    if (onCambio) onCambio();
  };

  const salvaNota = async () => {
    setSalvo(true);
    try {
      const { data } = await api.put(`/assunzione/${emp.id}`, { nota });
      aggiorna(data);
      toast.success("Nota salvata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSalvo(false); }
  };

  const dichiara = async (codice, data_) => {
    try {
      const { data } = await api.put(`/assunzione/${emp.id}`,
                                     { [codice]: { data: data_ || null } });
      aggiorna(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const chiudiPratica = async () => {
    try {
      const { data } = await api.post(`/assunzione/${emp.id}/chiudi`);
      aggiorna(data);
      toast.success("Pratica chiusa: ora si può mandare in cantiere");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const riapri = async () => {
    try {
      const { data } = await api.post(`/assunzione/${emp.id}/riapri`);
      aggiorna(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const togliDeroga = async () => {
    try {
      const { data } = await api.delete(`/assunzione/${emp.id}/deroga`);
      aggiorna(data);
      toast.success("Deroga revocata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardHat className="w-5 h-5 text-orange-500" />
            Pratica di assunzione · {emp.full_name}
          </DialogTitle>
        </DialogHeader>

        {carico && (
          <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
          </p>
        )}

        {!carico && pratica && !pratica.soggetta && (
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 text-sm text-slate-600">
            Questa persona è stata assunta prima del {it(pratica.da_quando_vale)}:
            la pratica non si applica e si assegna ai cantieri come sempre.
            Documenti e attestati restano quelli del fascicolo.
          </div>
        )}

        {!carico && pratica && pratica.soggetta && (
          <div className="space-y-3">
            <Intestazione pratica={pratica} />

            <div className="space-y-2">
              {pratica.righe.map((r) => (
                <Riga key={r.codice} riga={r} emp={emp}
                      onFirma={() => setFirma(r.codice)}
                      onCarica={setCarica}
                      onDichiara={dichiara} />
              ))}
            </div>

            <div>
              <Label className="text-xs">Nota della pratica</Label>
              <p className="text-[11px] text-slate-500 mb-1">
                Serve a chi la riprende: «visita medica prenotata per il 12»,
                «attestato in arrivo dal consulente».
              </p>
              <Textarea value={nota} onChange={(e) => setNota(e.target.value)}
                        rows={2} data-testid="nota-pratica"
                        placeholder="A che punto siamo" />
              <Button variant="outline" size="sm" className="mt-1.5" onClick={salvaNota}
                      disabled={salvo}>
                {salvo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Salva la nota
              </Button>
            </div>

            {pratica.deroga && (
              <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 text-sm">
                <div className="flex items-start gap-2">
                  <Unlock className="w-4 h-4 text-purple-700 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-purple-900">
                      Deroga in corso fino al {it(pratica.deroga.fino_al)}
                    </div>
                    <p className="text-purple-800 text-xs mt-0.5">
                      {pratica.deroga.motivo}
                    </p>
                    <p className="text-[11px] text-purple-700 mt-1">
                      Registrata da {pratica.deroga.da}
                      {pratica.deroga.mancava?.length > 0 && (
                        <> · mancava: {pratica.deroga.mancava.join(", ").toLowerCase()}</>
                      )}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={togliDeroga}>
                      Revoca
                    </Button>
                  )}
                </div>
              </div>
            )}

            {isAdmin && !pratica.completa && !pratica.deroga && (
              deroga
                ? <ModuloDeroga emp={emp} onFatto={(d) => { aggiorna(d); setDeroga(false); }}
                                onAnnulla={() => setDeroga(false)} />
                : (
                  <Button variant="outline" size="sm" onClick={() => setDeroga(true)}
                           data-testid="apri-deroga" className="text-purple-700">
                    <Unlock className="w-3 h-3 mr-1" />
                    Deroga: mandalo in cantiere comunque
                  </Button>
                )
            )}
          </div>
        )}

        {!carico && pratica?.soggetta && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onChiudi}>Chiudi la finestra</Button>
            {pratica.chiusa ? (
              <Button variant="outline" onClick={riapri} data-testid="riapri-pratica">
                Riapri la pratica
              </Button>
            ) : (
              <Button onClick={chiudiPratica} disabled={!pratica.completa}
                      data-testid="chiudi-pratica"
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                <FileCheck2 className="w-4 h-4 mr-2" />
                {pratica.completa
                  ? "Concludi la pratica"
                  : `Manca ancora ${pratica.mancano.length}`}
              </Button>
            )}
          </DialogFooter>
        )}

        {firma && (
          <DialogFirma emp={emp} tipo={firma} onChiudi={() => setFirma(null)}
                       onFatto={(d) => { setFirma(null); aggiorna(d); }} />
        )}
        {carica && (
          <DialogCaricaDocumento emp={emp} modo={carica}
                                 onChiudi={() => setCarica(null)}
                                 onFatto={() => { setCarica(null); leggi(); if (onCambio) onCambio(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

const it = (iso) => (iso ? String(iso).substring(0, 10).split("-").reverse().join("/") : "");

function Intestazione({ pratica }) {
  if (pratica.chiusa && pratica.in_attesa.length === 0) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-emerald-900">
            Pratica conclusa: si può mandare in cantiere
          </div>
          <div className="text-xs text-emerald-800">
            Chiusa il {it(pratica.chiusa_il)}
            {pratica.chiusa_da && ` da ${pratica.chiusa_da}`}
          </div>
        </div>
      </div>
    );
  }
  if (pratica.chiusa) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex items-center gap-2">
        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-amber-900">
            Pratica conclusa, ma {pratica.in_attesa.length === 1
              ? "un documento deve ancora arrivare"
              : `${pratica.in_attesa.length} documenti devono ancora arrivare`}
          </div>
          <div className="text-xs text-amber-800">
            {pratica.in_attesa.join(", ")}. Si assegna ai cantieri, ma la
            pratica resta segnalata finché i documenti non sono a fascicolo.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-center gap-2">
      <Lock className="w-5 h-5 text-red-600 flex-shrink-0" />
      <div className="text-sm">
        <div className="font-semibold text-red-900">
          In cantiere non ci può andare: {pratica.mancano.length === 1
            ? "manca un vincolo"
            : `mancano ${pratica.mancano.length} vincoli`}
        </div>
        <div className="text-xs text-red-800">
          {pratica.mancano.join(" · ").toLowerCase()}
        </div>
      </div>
    </div>
  );
}

const SLUG_DUE_DILIGENCE = "sgm-m8-1-3-1-4";

function Riga({ riga, emp, onFirma, onCarica, onDichiara }) {
  const segno = SEGNO[riga.esito] || SEGNO.manca;
  const Icona = segno.icona;
  const [data, setData] = useState("");

  return (
    <div className={`border rounded-lg p-3 ${segno.riquadro}`}
         data-testid={`riga-${riga.codice}`}>
      <div className="flex items-start gap-2">
        <Icona className={`w-5 h-5 flex-shrink-0 mt-0.5 ${segno.classe}`} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm leading-tight">{riga.nome}</div>
          <div className="text-xs text-slate-600 mt-0.5">{riga.dettaglio}</div>
          <div className="text-[11px] text-slate-500 mt-1">{riga.perche}</div>
          {riga.nota && (
            <div className="text-[11px] text-slate-500 italic mt-0.5">{riga.nota}</div>
          )}

          {riga.dichiarabile && riga.esito === "manca" && (
            <div className="flex items-end gap-2 mt-2 flex-wrap">
              <div>
                <Label className="text-[11px]">{riga.etichetta_data}</Label>
                <Input type="date" value={data} className="h-9 w-40"
                       data-testid={`data-${riga.codice}`}
                       onChange={(e) => setData(e.target.value)} />
              </div>
              <Button size="sm" variant="outline" className="h-9" disabled={!data}
                      onClick={() => onDichiara(riga.codice, data)}>
                Dichiara
              </Button>
              <Button size="sm" variant="outline" className="h-9"
                      onClick={() => onCarica("personale")}>
                <Upload className="w-3 h-3 mr-1" /> Carica il documento
              </Button>
            </div>
          )}
          {riga.dichiarabile && riga.esito === "in_attesa" && (
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-9"
                      onClick={() => onCarica("personale")}>
                <Upload className="w-3 h-3 mr-1" /> Carica il documento
              </Button>
              <Button size="sm" variant="outline" className="h-9"
                      onClick={() => onDichiara(riga.codice, null)}>
                Annulla la dichiarazione
              </Button>
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          {riga.codice === "dpi" && riga.esito !== "ok" && (
            <Link to={`/consegna?dipendente=${emp.id}`}>
              <Button size="sm" variant="outline">Vai alla consegna</Button>
            </Link>
          )}
          {(riga.codice === "tesserino" || riga.codice === "gps") && riga.esito !== "ok" && (
            <Button size="sm" onClick={onFirma}
                    data-testid={`firma-${riga.codice}`}
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              <PenLine className="w-3 h-3 mr-1" /> Firma
            </Button>
          )}
          {riga.codice === "due_diligence" && riga.esito !== "ok" && (
            <Link to={`/sistemi-integrati?scheda=schede&compila=${SLUG_DUE_DILIGENCE}&persona=${emp.id}`}>
              <Button size="sm" variant="outline">
                <ScrollText className="w-3 h-3 mr-1" /> Compila
              </Button>
            </Link>
          )}
          {riga.codice === "formazione" && riga.esito !== "ok" && (
            <Button size="sm" variant="outline" onClick={() => onCarica("attestato")}>
              <Upload className="w-3 h-3 mr-1" /> Attestato
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * I due fogli che si firmano qui: il verbale del tesserino e l'informativa
 * sulla localizzazione dei mezzi. Due firme sul tablet, il lavoratore e il
 * legale rappresentante, e il PDF si archivia da se' nel fascicolo.
 */
function DialogFirma({ emp, tipo, onChiudi, onFatto }) {
  const gps = tipo === "gps";
  const [testo, setTesto] = useState(null);
  const [lavoratore, setLavoratore] = useState(null);
  const [azienda, setAzienda] = useState(null);
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!gps) return;
    api.get("/assunzione/informativa-gps")
      .then(({ data }) => {
        setTesto(data);
        setNome(data.azienda?.legale_rappresentante || "");
      })
      .catch(() => toast.error("Errore nel caricamento dell'informativa"));
  }, [gps]);

  const invia = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/assunzione/${emp.id}/${gps ? "informativa-gps" : "tesserino"}`,
        { firma_lavoratore: lavoratore, firma_azienda: azienda, nome_azienda: nome });
      toast.success("Firmato e archiviato nel fascicolo");
      onFatto(data.pratica);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {gps ? "Informativa sulla localizzazione dei mezzi"
                 : "Verbale di consegna del tesserino"}
            {" · "}{emp.full_name}
          </DialogTitle>
        </DialogHeader>

        {gps && (
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 max-h-64 overflow-y-auto">
            {!testo && (
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Caricamento del testo...
              </p>
            )}
            {testo && (
              <>
                <div className="font-semibold text-sm">{testo.titolo}</div>
                <div className="text-[11px] text-slate-500 mb-2">{testo.sottotitolo}</div>
                {testo.blocchi.map((b, i) => (
                  <div key={i} className="mb-1.5">
                    {b.titolo && (
                      <div className="text-xs font-semibold text-[#0067b2] mt-2">
                        {b.titolo}
                      </div>
                    )}
                    <div className={`text-[11px] whitespace-pre-line ${
                      b.rilievo ? "font-semibold text-center" : "text-slate-700"} ${
                      b.sotto ? "pl-6" : b.punto ? "pl-3" : ""}`}>
                      {(b.sotto || b.punto) && "• "}{b.testo}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {!gps && (
          <p className="text-sm text-slate-600">
            Il lavoratore dichiara di aver ricevuto il tesserino di
            riconoscimento personale
            {emp.badge_number && <> n. <strong>{emp.badge_number}</strong></>},
            di esporlo in modo visibile in cantiere e di riconsegnarlo alla
            cessazione del rapporto. Il verbale esce con il marchio e si
            archivia fra i suoi documenti personali.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CampoFirma valore={lavoratore} onChange={setLavoratore} obbligatoria
                      etichetta={gps ? "Firma del dipendente" : "Firma del lavoratore"}
                      descrizione={emp.full_name} />
          <div>
            <CampoFirma valore={azienda} onChange={setAzienda} obbligatoria
                        etichetta={gps ? "Firma del legale rappresentante"
                                       : "Firma per l'azienda"} />
            <Label className="text-xs mt-2 block">Nome di chi firma per l&apos;azienda</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)}
                   className="h-9" placeholder="Nome e cognome" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={invia} disabled={busy || !lavoratore || !azienda}
                  data-testid="salva-firme"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <PenLine className="w-4 h-4 mr-2" />}
            Firma e archivia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Il documento che arriva da fuori: idoneità, UNILAV, attestato del corso. */
function DialogCaricaDocumento({ emp, modo, onChiudi, onFatto }) {
  const attestato = modo === "attestato";
  const [f, setF] = useState({
    category: attestato ? "Formazione specifica" : "Idoneità Sanitarie",
    title: "", issued_date: "", expires_date: "", issuer: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const categorie = attestato
    ? ["Formazione specifica", "Formazione generale"]
    : ["Idoneità Sanitarie", "UNILAV"];

  const invia = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    fd.append("employee_id", emp.id);
    fd.append("doc_class", attestato ? "attestato" : "personale");
    Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
    if (!f.title) fd.set("title", f.category);
    if (file) fd.append("file", file);
    try {
      await api.post("/certificates", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento archiviato nel fascicolo");
      onFatto();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {attestato ? "Attestato di formazione" : "Documento personale"}
            {" · "}{emp.full_name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={invia} className="space-y-3">
          <div>
            <Label>Tipo</Label>
            <select value={f.category} className="w-full h-10 border border-slate-200 rounded px-3"
                    data-testid="categoria-documento"
                    onChange={(e) => setF({ ...f, category: e.target.value })}>
              {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Titolo (facoltativo)</Label>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                   placeholder={f.category} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Data del documento</Label>
              <Input type="date" value={f.issued_date}
                     onChange={(e) => setF({ ...f, issued_date: e.target.value })} />
            </div>
            <div>
              <Label>Scadenza</Label>
              <Input type="date" value={f.expires_date}
                     onChange={(e) => setF({ ...f, expires_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files[0])}
                   accept=".pdf,.jpg,.jpeg,.png" />
            <p className="text-[11px] text-slate-500 mt-1">
              Senza file il documento resta registrato ma senza allegato: per
              l&apos;ispezione serve il file.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onChiudi}>Annulla</Button>
            <Button type="submit" disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Upload className="w-4 h-4 mr-2" />}
              Archivia
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** La deroga: va scritto perché, e resta scritto. */
function ModuloDeroga({ emp, onFatto, onAnnulla }) {
  const [motivo, setMotivo] = useState("");
  const [fino, setFino] = useState("");
  const [busy, setBusy] = useState(false);

  const invia = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/assunzione/${emp.id}/deroga`,
                                      { motivo, fino_al: fino || null });
      toast.success("Deroga registrata");
      onFatto(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
        <ShieldAlert className="w-4 h-4" /> Deroga dell&apos;amministratore
      </div>
      <p className="text-xs text-purple-800">
        Lo rende assegnabile anche con la pratica aperta. Il motivo resta
        registrato sulla persona e si vede nei controlli del cantiere: serve a
        poter spiegare, non a far finta che sia tutto in ordine.
      </p>
      <div>
        <Label className="text-xs">Perché</Label>
        <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                  data-testid="motivo-deroga"
                  placeholder="Corso art. 37 prenotato per il 9 ottobre, serve in cantiere lunedì" />
      </div>
      <div>
        <Label className="text-xs">Valida fino al (se lasci vuoto: 30 giorni)</Label>
        <Input type="date" value={fino} onChange={(e) => setFino(e.target.value)}
               className="h-9 w-44" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onAnnulla}>Annulla</Button>
        <Button size="sm" onClick={invia} disabled={busy || motivo.trim().length < 10}
                data-testid="salva-deroga"
                className="bg-purple-600 hover:bg-purple-700 text-white">
          Registra la deroga
        </Button>
      </div>
    </div>
  );
}
